import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null); // null | "success" | "exists" | "error"
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email, name });
      setStatus(data.message === "Already subscribed" ? "exists" : "success");
      if (data.message !== "Already subscribed") { setEmail(""); setName(""); }
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  return (
    <section data-testid="newsletter-section" className="py-16 px-6 bg-navy/5 border-y border-slate-100">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-navy" />
            <span className="font-body text-navy text-xs uppercase tracking-[0.15em] font-bold">Newsletter</span>
          </div>
          <h3 className="font-heading font-bold text-xl sm:text-2xl text-navy mb-2">
            Stay Connected
          </h3>
          <p className="font-body text-slate-800 text-sm mb-8 max-w-md mx-auto">
            Subscribe to our monthly newsletter for updates, event announcements, and words of encouragement.
          </p>

          {status === "success" ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex flex-col items-center gap-3"
            >
              <CheckCircle className="w-12 h-12 text-cgreen" />
              <p className="font-body text-navy font-semibold">You're subscribed! God bless you.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto" data-testid="newsletter-form">
              <Input
                data-testid="newsletter-name-input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-full border-slate-200 font-body"
              />
              <Input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
                className="rounded-full border-slate-200 font-body"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="rounded-full bg-navy hover:bg-navy/90 text-white font-body px-8 shrink-0"
              >
                {loading ? "..." : "Subscribe"}
              </Button>
            </form>
          )}
          {status === "exists" && <p className="font-body text-slate-500 text-sm mt-3">You're already subscribed!</p>}
          {status === "error" && <p className="font-body text-crimson text-sm mt-3">Something went wrong. Please try again.</p>}
        </motion.div>
      </div>
    </section>
  );
}
